import { Alert } from 'bold-ui'
import {
  Chart,
  ChartBody,
  ChartContainer,
  ChartFooter,
  DateRange,
  ReferenceArea,
  SeriesType,
  ValueRange,
} from 'bold-ui/lib/components/Chart'
import { TickProps } from 'bold-ui/lib/components/Chart/Tick'
import { blue, gray, green, red } from 'bold-ui/lib/styles/colors'
import { SexoEnum } from 'graphql/types.generated'
import moment, { Duration, Moment } from 'moment'
import { useCallback, useMemo } from 'react'
import { filterGestacoesPeriodo } from 'util/atendimento/gestante/filterGestacoesPeriodo'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/model-historicoMedicao'

import { PeriodoGestacaoModel } from '../../../types/PeriodoGestacaoModel'
import { getEndGestacao } from '../../pre-natal/util-medicoesPreNatal'
import { reduceMedicoesIguais } from '../../util-medicoes'
import { getReferenceAreasCalculators, ReferenceAreasCalculator } from './referenceAreaCalculators'
import { TickIdade } from './TickIdade'
import { TooltipGraficoAntropometria } from './TooltipGraficoAntropometria'
import { buildAreasPreTermo, dataNascimentoPreTermo, PeriodoPreTermo } from './util-grafico-pre-termo'

export interface GraficoPesoProps {
  medicoes: HistoricoMedicaoModel[]
  dataNascimento: Moment
  dataRange: DateRange
  sexo: SexoEnum
  gestacoes?: ReadonlyArray<PeriodoGestacaoModel>
  isGestante?: boolean
  periodoPreTermo?: Readonly<PeriodoPreTermo>
  shouldRenderPreTermo?: boolean
}

// TODO@CODEBENDERS - Adicionar funcionalidade de outliers #23674
export function GraficoPesoView(props: Readonly<GraficoPesoProps>) {
  const {
    medicoes,
    dataNascimento,
    dataRange,
    sexo,
    gestacoes = [],
    isGestante = false,
    periodoPreTermo,
    shouldRenderPreTermo = false,
  } = props

  const dataNascimentoAjustada =
    shouldRenderPreTermo && periodoPreTermo ? dataNascimentoPreTermo(periodoPreTermo) : moment(dataNascimento)
  const maxIdadeNoRange = moment.duration(moment(dataRange.end).diff(dataNascimentoAjustada))
  const medicoesComPeso = reduceMedicoesIguais(medicoes, 'valorPeso') ?? []
  const seriesData = medicoesComPeso.map((m) => ({
    x: m.dataMedicao,
    y: m.valorPeso,
  }))
  const yRange = getYRangePadrao(maxIdadeNoRange)
  const gestacoesPeriodo = filterGestacoesPeriodo(gestacoes, moment(dataRange.init), moment(dataRange.end))

  const areasGestacao = useMemo(() => {
    const inicioGrafico = moment(dataRange.init).startOf('day').add(1, 'day')
    const fimGrafico = moment(dataRange.end).startOf('day')

    return gestacoesPeriodo.map((periodo) => {
      const inicioGestacao = moment(periodo.inicio).startOf('day')
      const fimGestacao = getEndGestacao(periodo)

      const inicioGestacaoAjustado = inicioGestacao.isBefore(inicioGrafico) ? inicioGrafico : inicioGestacao
      const fimGestacaoAjustado = fimGestacao.isAfter(fimGrafico) ? fimGrafico : fimGestacao

      return {
        name: 'Gestação',
        init: inicioGestacaoAjustado,
        end: fimGestacaoAjustado,
        fillColor: 'none',
        tickColor: blue.c60,
        strokeColor: gray.c40,
        tooltip: 'O gráfico de peso não deve ser considerado no período da gestação.',
        mask: {
          show: true,
          hideDots: {
            from: inicioGestacaoAjustado,
            to: fimGestacaoAjustado,
          },
          fillOpacity: 1,
          overlayDots: true,
        },
      }
    })
  }, [gestacoesPeriodo, dataRange])

  const renderRangeAreas = () => {
    const areasPreTermo = !shouldRenderPreTermo && periodoPreTermo ? buildAreasPreTermo(periodoPreTermo, dataRange) : []

    return [...areasGestacao, ...areasPreTermo]
  }

  const tickRenderer = useCallback(
    (tickProps: TickProps) => (
      <TickIdade {...tickProps} dataNascimento={dataNascimentoAjustada} shouldRenderPreTermo={shouldRenderPreTermo} />
    ),
    [dataNascimentoAjustada, shouldRenderPreTermo]
  )
  const yAxis = shouldRenderPreTermo ? yAxisPreTermo : { title: 'Peso', unit: 'kg', domain: yRange }

  const tituloEixoX = shouldRenderPreTermo ? 'Semanas' : 'Idade (meses completos e anos)'

  return (
    <>
      {isGestante && (
        <Alert type='info' styles={{ container: styles.iconAlertImcGestante }} style={styles.alertImcGestante}>
          O peso não é recomendado como indicador durante a gestação. Utilize o gráfico de ganho de peso para
          monitoramento adequado.
        </Alert>
      )}
      <ChartContainer>
        <ChartBody height={580}>
          <Chart<Moment>
            outliers='auto'
            type={SeriesType.Area}
            series={[{ name: 'Peso', data: seriesData }]}
            referenceAreas={getReferenceAreas(
              sexo,
              dataRange,
              maxIdadeNoRange,
              dataNascimentoAjustada,
              shouldRenderPreTermo
            )}
            xAxis={{
              title: tituloEixoX,
              domain: dataRange,
              tickRenderer,
            }}
            rangeAreas={renderRangeAreas()}
            yAxis={yAxis}
            showLegend={false}
            tooltip={{
              type: 'point',
              render: (points) => (
                <TooltipGraficoAntropometria
                  label='Peso'
                  unidadeDeMedida='kg'
                  points={points}
                  dataNascimento={dataNascimento}
                  shouldRenderPreTermo={shouldRenderPreTermo}
                />
              ),
            }}
          />
        </ChartBody>
        <ChartFooter>{getFooterText(maxIdadeNoRange, shouldRenderPreTermo)}</ChartFooter>
      </ChartContainer>
    </>
  )
}

function getFooterText(idadeNoRange: Duration, shouldRenderPreTermo: boolean): string {
  if (shouldRenderPreTermo) {
    return 'Fonte: INTERGROWTH-21ˢᵗ - 2026.'
  }

  if (idadeNoRange.years() <= 10) {
    return 'Fonte: Adaptado do Guia para a Organização da Vigilância Alimentar e Nutricional na Atenção Primária à Saúde, 2023.'
  }

  return ''
}

const yRangePreTermo: ValueRange = {
  init: 0.0,
  end: 12.0,
  step: 0.5,
}

const yAxisPreTermo = {
  title: 'Peso',
  unit: 'kg',
  domain: yRangePreTermo,
  tickRenderer: ({ payload, ...rest }: TickProps) => <text {...rest}>{Number(payload.value).toFixed(1)}</text>,
}

function getYRangePadrao(maxIdadeNoRange: Duration): ValueRange {
  if (maxIdadeNoRange.years() <= 2) return { init: 0, end: 20, step: 1 }
  else if (maxIdadeNoRange.years() <= 5) return { init: 7, end: 30, step: 1 }
  else if (maxIdadeNoRange.years() <= 10) return { init: 10, end: 65, step: 5 }
  else if (maxIdadeNoRange.years() <= 15) return { init: 25, end: 150, step: 5 }
  else return { init: 25, end: 150, step: 5 }
}

function getReferenceAreas(
  sexo: SexoEnum,
  xRange: DateRange,
  idadeNoRange: Duration,
  dataNascimento: Moment,
  shouldRenderPreTermo: boolean
): ReferenceArea<Moment>[] {
  const refAreaCalculators = getReferenceAreasCalculators(sexo, dataNascimento, xRange, 'peso', shouldRenderPreTermo)

  if (shouldRenderPreTermo) {
    return [
      getReferenceAreaPreTermoRiscoInferior(refAreaCalculators),
      getReferenceAreaPreTermoAdequado(refAreaCalculators),
      getReferenceAreaPreTermoRiscoSuperior(refAreaCalculators),
    ]
  }

  if (idadeNoRange.years() <= 10) {
    return [
      getReferenceAreaMuitoBaixo(refAreaCalculators),
      getReferenceAreaBaixo(refAreaCalculators),
      getReferenceAreaAdequado(refAreaCalculators),
      getReferenceAreaElevado(refAreaCalculators),
    ]
  }

  return undefined
}

function getReferenceAreaMuitoBaixo(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date, referenceAreaCalculator }) => ({
      x: date,
      upperLimit: referenceAreaCalculator(-3),
    })),
    label: {
      name: 'Muito baixo',
    },
    color: gray.c90,
    stroke: {
      color: gray.c40,
    },
    tick: {
      color: gray.c40,
    },
  }
}

function getReferenceAreaBaixo(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date, referenceAreaCalculator }) => ({
      x: date,
      upperLimit: referenceAreaCalculator(-2),
    })),
    label: {
      name: 'Baixo',
    },
    color: red.c90,
    stroke: {
      color: red.c60,
    },
    tick: {
      color: red.c60,
    },
  }
}

function getReferenceAreaAdequado(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date, referenceAreaCalculator }) => ({
      x: date,
      upperLimit: referenceAreaCalculator(2),
    })),
    label: {
      name: 'Adequado',
    },
    color: green.c90,
    stroke: {
      color: red.c60,
    },
    tick: {
      color: green.c60,
    },
  }
}

function getReferenceAreaElevado(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date }) => ({
      x: date,
    })),
    label: {
      name: 'Elevado',
    },
    color: red.c90,
    tick: {
      color: red.c60,
    },
  }
}

function getReferenceAreaPreTermoRiscoInferior(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date, referenceAreaCalculator }) => ({
      x: date,
      upperLimit: referenceAreaCalculator(-2),
    })),
    label: { name: 'Risco', color: red.c50 },
    color: red.c90,
    stroke: { color: red.c60 },
    tick: { color: red.c60 },
  }
}

function getReferenceAreaPreTermoAdequado(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date, referenceAreaCalculator }) => ({
      x: date,
      upperLimit: referenceAreaCalculator(2),
    })),
    label: { name: 'Adequado', color: green.c40 },
    color: green.c90,
    stroke: { color: red.c60 },
    tick: { color: green.c60 },
  }
}

function getReferenceAreaPreTermoRiscoSuperior(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date }) => ({
      x: date,
    })),
    // Se houver dois ReferenceAreas com a mesma label, o grafico se desconfigura.
    // Usamos um caractere invisivel (U+200B) para diferenciar, pois a label oficial
    // nao pode ser alterada. Isso sera resolvido na issue #31237.
    label: { name: 'Risco\u200B', color: red.c50 },
    color: red.c90,
    stroke: { color: 'none' },
    tick: { color: red.c60 },
  }
}

const styles = {
  alertImcGestante: {
    marginBottom: '1rem',
  },
  iconAlertImcGestante: {
    svg: {
      fontSize: '1rem',
    },
  },
}
